import React, { useState } from 'react';
import { Grid, Typography, Card, CardHeader, CardContent } from '@mui/material';

import { useApiGet, useApiSend } from '../../../utils/httpClient';
import {
  getSPDLeadership,
  patchSPDLeadership,
  postSPDLeadership,
  getSPDLeadershipPositions,
  deleteSPDLeadership,
  replaceExistingLeadership,
} from '../../../endpoints/chapters';
import { roles } from '../../../utils/variables';
import PositionDisplay from '../../../Components/PositionDisplay';
import AddPosition from '../../../Components/AddPosition';

const SPDLeadership = ({ id, spdAccess }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [errorSaving, setErrorSaving] = useState(null);

  const {
    data: leadershipData,
    isFetching,
    refetch,
  } = useApiGet(
    ['spd-leadership', id],
    getSPDLeadership,
    {
      enabled: true,
      retry: 1,
    },
    {
      id,
    }
  );

  const { data: positionsData } = useApiGet(
    ['spd-leadership-positions'],
    getSPDLeadershipPositions,
    {
      enabled: true,
      retry: 1,
    },
    {}
  );

  const { mutate: updateLeadership } = useApiSend(
    patchSPDLeadership,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: deleteLeadership } = useApiSend(
    deleteSPDLeadership,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );
  const { mutate: postLeadership } = useApiSend(
    postSPDLeadership,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: replaceLeadership } = useApiSend(
    replaceExistingLeadership,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const onSavePosition = (position) => {
    setIsSaving(true);
    setErrorSaving(null);
    if (position.action === 'newAppointment') {
      //Handle post
      if (!position.id) {
        //Handle new appointment
        postLeadership({
          formId: id,
          user_id: position.userId,
          start_date: position.startDate,
          end_date: null,
          position_id: position.positionId,
          committee_id: position.committeeId,
        });
      } else {
        replaceLeadership({
          id: position.id,
          user_id: position.userId,
          start_date: position.startDate,
          end_date: null,
        });
      }
    } else if (position.action === 'editDates') {
      //Handle patch
      updateLeadership({
        formId: id,
        id: position.id,
        start_date: position.startDate,
        end_date: position.endDate,
      });
    } else if (position.action === 'removeRecord') {
      //Handle delete
      deleteLeadership({
        formId: id,
        id: position.id,
      });
    }
  };

  if (isFetching) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }

  if (!leadershipData) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">No data available</Typography>
      </Grid>
    );
  }
  const canEdit = () => {
    if (spdAccess) {
      let userRole = localStorage.getItem('role');
      let rolesAllowed = [roles.Admin, roles.Support, roles.SPDOfficer];
      return rolesAllowed.includes(userRole);
    }
    return false;
  };

  return (
    <Grid container>
      <Grid item xs={12} md={6} padding>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title="Current Officers" />
          <CardContent>
            <Grid container spacing={2}>
              {leadershipData.officers
                ?.sort((a, b) => a.order - b.order)
                .map((officer) => (
                  <PositionDisplay
                    id={officer.id}
                    position={officer.position}
                    positionId={officer.position_id}
                    committeeId={officer.committee_id}
                    name={officer.name}
                    startDate={officer.start_date}
                    endDate={officer.end_date}
                    canEdit={canEdit()}
                    isSaving={isSaving}
                    errorSavingText={errorSaving}
                    onSave={onSavePosition}
                    userId={officer.user_id}
                    key={officer.id}
                    pageFrom={'spd-leadership'}
                    spdChapterId={id}
                  />
                ))}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6} padding>
        <Grid item container xs={12}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2, width: '100%' }}>
            <CardHeader title="Executive Assistants" sx={{ p: 1 }} />
            <CardContent sx={{ p: 1 }}>
              <Grid container spacing={2}>
                {leadershipData.assistants
                  ?.sort((a, b) => a.order - b.order)
                  .map((assistant) => (
                    <PositionDisplay
                      id={assistant.id}
                      position={assistant.position}
                      positionId={assistant.position_id}
                      committeeId={assistant.committee_id}
                      name={assistant.name}
                      startDate={assistant.start_date}
                      endDate={assistant.end_date}
                      canEdit={canEdit()}
                      isSaving={isSaving}
                      errorSavingText={errorSaving}
                      onSave={onSavePosition}
                      userId={assistant.user_id}
                      pageFrom={'spd-leadership'}
                      key={assistant.id}
                      spdChapterId={id}
                    />
                  ))}
                {leadershipData.assistants.length === 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body1" textAlign={'center'}>
                      No assistants assigned
                    </Typography>
                  </Grid>
                )}
                {canEdit() && (
                  <Grid item xs={12} padding>
                    <AddPosition
                      buttonText={'Add Executive Assistant'}
                      positions={positionsData}
                      committeeId={leadershipData?.officers[0]?.committee_id}
                      onAdd={onSavePosition}
                      isSaving={isSaving}
                      savingError={errorSaving}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item container xs={12} paddingTop={2}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2, width: '100%' }}>
            <CardHeader title="QBE Users" sx={{ p: 1 }} />
            <CardContent sx={{ p: 1 }}>
              <Grid container spacing={2}>
                {leadershipData.qbe_users.map((user) => (
                  <PositionDisplay
                    id={user.id}
                    position={user.position}
                    positionId={user.position_id}
                    committeeId={user.committee_id}
                    name={user.name}
                    startDate={user.start_date}
                    endDate={user.end_date}
                    canEdit={canEdit()}
                    isSaving={isSaving}
                    errorSavingText={errorSaving}
                    onSave={onSavePosition}
                    userId={user.user_id}
                    pageFrom={'spd-leadership'}
                    key={user.id}
                    spdChapterId={id}
                  />
                ))}
                {leadershipData.qbe_users.length === 0 && (
                  <Grid item xs={12} textAlign={'center'}>
                    <Typography variant="body1">
                      No QBE users assigned
                    </Typography>
                  </Grid>
                )}
                {canEdit() && (
                  <Grid item xs={12} padding>
                    <AddPosition
                      buttonText={'Add QBE User'}
                      positions={[
                        {
                          id: 'd4f0550fadc24f9a8d0c6dee5f188fb9',
                          name: 'Member',
                        },
                      ]}
                      committeeId={'a09b2d7351de4b0b8723fd0c26a080b6'}
                      onAdd={onSavePosition}
                      isSaving={isSaving}
                      savingError={errorSaving}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SPDLeadership;
